import { useMutation } from '@apollo/client';
import { AlertBannerTransitions } from '@generated/graphql';

import { Menu } from '@ovComponents/2-component/menu/menu';
import { MenuItem } from '@ovComponents/2-component/menuItem/menuItem';
import { TableCell } from '@ovComponents/2-component/table/table';
import { ConfirmationModal } from '@ovComponents/3-pattern/confirmationDialog/confirmationDialog';
import { UserContext } from '@providers/userContextProvider';
import { IconButton, useSnackbar } from 'hephaestus/web';
import { MoreVert } from 'hephaestus/webIcons';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DELETE_ALERT_BANNER,
  TRANSITION_ALERT_BANNER,
} from '../alertBanners.queries';
import { useAlertBannerPermissions } from '../hooks/useAlertBannerPermissions';
import { type AlertBannerRow, AlertBannerStatus } from '../types';

export default function AlertBannerActionCell({
  isFirst,
  alertBanner,
  refetch,
}: {
  isFirst?: boolean;
  alertBanner: AlertBannerRow;
  refetch: () => void;
}) {
  const { t } = useTranslation(['alertBanners']);
  const { enqueueSnackbar } = useSnackbar();
  const { activeOrganization } = useContext(UserContext);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [open, setOpen] = useState(false);
  const { hasWriteBannersPermission, hasTransitionBannersPermissions } =
    useAlertBannerPermissions();

  const [deleteAlertBannerMutation, { loading: deleteAlertBannerLoading }] =
    useMutation(DELETE_ALERT_BANNER);
  const [transitionAlertBannerMutation] = useMutation(TRANSITION_ALERT_BANNER);

  const deleteAlertBanner = async (e?: React.MouseEvent<HTMLElement>) => {
    e?.stopPropagation();
    await deleteAlertBannerMutation({
      variables: {
        input: {
          alertBannerId: alertBanner.id,
        },
      },
      onCompleted: () => {
        handleClose();
        enqueueSnackbar(t('alertBannerDeleteModal.deletedSuccessfully'), {
          variant: 'success',
        });
      },
    });
    refetch();
  };

  const setToDraftAlertBanner = async () => {
    await transitionAlertBannerMutation({
      variables: {
        input: {
          alertBannerId: alertBanner.id,
          transition: AlertBannerTransitions.Unpublish,
        },
      },
      onCompleted: () => {
        enqueueSnackbar(t('alertBannerDeleteModal.setToDraftSuccessfully'), {
          variant: 'success',
        });
      },
    });
    refetch();
  };

  const handleClose = (e?: React.MouseEvent<HTMLElement>) => {
    e?.stopPropagation();
    setAnchorEl(null);
    setOpen(false);
  };

  const canTransitionBanner =
    [AlertBannerStatus.ACTIVE, AlertBannerStatus.SCHEDULED].includes(
      alertBanner.status,
    ) && hasTransitionBannersPermissions;

  const shouldShowToggleMenu = hasWriteBannersPermission || canTransitionBanner;

  return (
    <>
      <TableCell isFirst={isFirst} right>
        {alertBanner.organization.id === activeOrganization.id &&
          shouldShowToggleMenu && (
            <IconButton
              aria-label='more actions'
              onClick={(e) => {
                e.stopPropagation();
                setAnchorEl(e.currentTarget);
              }}
            >
              <MoreVert fontSize='inherit' />
            </IconButton>
          )}
      </TableCell>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={(e: React.MouseEvent<HTMLElement>) => handleClose(e)}
        onClick={handleClose}
        sx={{
          '& .MuiPaper-root': {
            borderRadius: 2,
          },
          '& .MuiList-root': {
            paddingY: '2px',
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {[AlertBannerStatus.ACTIVE, AlertBannerStatus.SCHEDULED].includes(
          alertBanner.status,
        ) &&
          hasTransitionBannersPermissions && (
            <MenuItem
              onClick={(e: React.MouseEvent<HTMLElement>) => {
                e.stopPropagation();
                setToDraftAlertBanner();
              }}
            >
              {t('table.actions.setToDraft')}
            </MenuItem>
          )}
        {hasWriteBannersPermission && (
          <MenuItem
            onClick={(e: React.MouseEvent<HTMLElement>) => {
              e.stopPropagation();
              setOpen(true);
            }}
          >
            {t('table.actions.delete')}
          </MenuItem>
        )}
      </Menu>
      <ConfirmationModal
        open={open}
        onCancel={handleClose}
        onConfirm={deleteAlertBanner}
        loading={deleteAlertBannerLoading}
        title={t('alertBannerDeleteModal.deleteAlertBanner')}
        bodyText={`${t('alertBannerDeleteModal.wantToProceed', {
          name: `"${alertBanner.name || t('alertBannerDeleteModal.untitled')}"`,
        })}`}
        confirmButtonLabel={t('table.actions.delete')}
      />
    </>
  );
}
